import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { auth } from '../services/firebase';

/**
 * Custom hook for managing real-time transcription in meetings
 * Handles WebSocket connection for audio streaming and transcript receiving
 * 
 * Uses Web Audio API to capture and convert audio to linear16 PCM format
 * which is required by Deepgram for transcription.
 * 
 * Mobile Compatibility:
 * - Uses AudioWorklet when available (modern browsers)
 * - Falls back to ScriptProcessorNode for older browsers
 * - Handles iOS Safari webkitAudioContext
 * - Resumes AudioContext on user gesture (mobile requirement)
 */
export function useTranscription(roomId, user) {
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcript, setTranscript] = useState([]);
    const [error, setError] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);

    const wsRef = useRef(null);
    const audioContextRef = useRef(null);
    const processorRef = useRef(null);
    const audioStreamRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const workletNodeRef = useRef(null);

    // Get backend WebSocket URL, including the Firebase ID token so the
    // backend can authenticate the connection (prevents anonymous abuse of
    // the transcription pipeline).
    const getWsUrl = useCallback(async () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = backendUrl.replace(/^https?:\/\//, '');
        let tokenParam = '';
        try {
            if (auth.currentUser) {
                const token = await auth.currentUser.getIdToken();
                tokenParam = `?token=${encodeURIComponent(token)}`;
            }
        } catch (e) {
            // No token available — backend will reject if it requires auth.
        }
        return `${wsProtocol}://${wsHost}/ws/transcribe/${roomId}${tokenParam}`;
    }, [roomId]);

    /**
     * Convert Float32Array audio samples to Int16 (linear16 PCM)
     * Deepgram requires linear16 PCM audio format
     */
    const floatTo16BitPCM = useCallback((float32Array) => {
        const buffer = new ArrayBuffer(float32Array.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < float32Array.length; i++) {
            // Clamp the value to prevent overflow
            let s = Math.max(-1, Math.min(1, float32Array[i]));
            // Convert to 16-bit integer
            view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
        return buffer;
    }, []);

    /**
     * Downsample audio from source sample rate to target sample rate
     * Browser audio is usually 44100Hz or 48000Hz, Deepgram expects 16000Hz
     */
    const downsampleBuffer = useCallback((buffer, sourceSampleRate, targetSampleRate) => {
        if (sourceSampleRate === targetSampleRate) {
            return buffer;
        }
        const sampleRateRatio = sourceSampleRate / targetSampleRate;
        const newLength = Math.round(buffer.length / sampleRateRatio);
        const result = new Float32Array(newLength);
        let offsetResult = 0;
        let offsetBuffer = 0;
        while (offsetResult < result.length) {
            const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
            let accum = 0, count = 0;
            for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
                accum += buffer[i];
                count++;
            }
            result[offsetResult] = accum / count;
            offsetResult++;
            offsetBuffer = nextOffsetBuffer;
        }
        return result;
    }, []);

    /**
     * Setup audio processing with ScriptProcessorNode (fallback for older browsers)
     */
    const setupScriptProcessor = useCallback((audioContext, source, ws, actualSampleRate) => {
        // Use smaller buffer size for lower latency (2048 instead of 4096)
        const bufferSize = 2048;
        const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
            if (ws.readyState !== WebSocket.OPEN) return;

            // Get audio data from input channel
            const inputData = e.inputBuffer.getChannelData(0);

            // Downsample to 16kHz if needed
            const downsampledData = actualSampleRate !== 16000
                ? downsampleBuffer(inputData, actualSampleRate, 16000)
                : inputData;

            // Convert to linear16 PCM format
            const pcmData = floatTo16BitPCM(downsampledData);

            // Send PCM data to backend
            ws.send(pcmData);
        };

        // Connect the audio pipeline: source -> processor -> destination
        source.connect(processor);
        processor.connect(audioContext.destination);

        console.log('Using ScriptProcessorNode for audio capture');
    }, [downsampleBuffer, floatTo16BitPCM]);

    /**
     * Setup audio processing with AudioWorklet (modern browsers, better for mobile)
     */
    const setupAudioWorklet = useCallback(async (audioContext, source, ws, actualSampleRate) => {
        try {
            // Create inline worklet processor code
            const workletCode = `
                class PCMProcessor extends AudioWorkletProcessor {
                    constructor() {
                        super();
                        this.bufferSize = 2048;
                        this.buffer = new Float32Array(this.bufferSize);
                        this.bufferIndex = 0;
                    }
                    
                    process(inputs, outputs, parameters) {
                        const input = inputs[0];
                        if (!input || !input[0]) return true;
                        
                        const inputData = input[0];
                        
                        for (let i = 0; i < inputData.length; i++) {
                            this.buffer[this.bufferIndex++] = inputData[i];
                            
                            if (this.bufferIndex >= this.bufferSize) {
                                this.port.postMessage({
                                    audioData: this.buffer.slice()
                                });
                                this.bufferIndex = 0;
                            }
                        }
                        
                        return true;
                    }
                }
                
                registerProcessor('pcm-processor', PCMProcessor);
            `;

            const blob = new Blob([workletCode], { type: 'application/javascript' });
            const workletUrl = URL.createObjectURL(blob);

            await audioContext.audioWorklet.addModule(workletUrl);
            URL.revokeObjectURL(workletUrl);

            const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
            workletNodeRef.current = workletNode;

            workletNode.port.onmessage = (event) => {
                if (ws.readyState !== WebSocket.OPEN) return;

                const { audioData } = event.data;

                // Downsample to 16kHz if needed
                const downsampledData = actualSampleRate !== 16000
                    ? downsampleBuffer(audioData, actualSampleRate, 16000)
                    : audioData;

                // Convert to linear16 PCM format
                const pcmData = floatTo16BitPCM(downsampledData);

                // Send PCM data to backend
                ws.send(pcmData);
            };

            // Connect the audio pipeline
            source.connect(workletNode);
            workletNode.connect(audioContext.destination);

            console.log('Using AudioWorklet for audio capture (better for mobile)');
            return true;
        } catch (err) {
            console.warn('AudioWorklet not available, falling back to ScriptProcessor:', err);
            return false;
        }
    }, [downsampleBuffer, floatTo16BitPCM]);

    // Start transcription
    const startTranscription = useCallback(async () => {
        if (isTranscribing || isConnecting) return;

        setIsConnecting(true);
        setError(null);

        try {
            // First, call the REST API to start transcription on backend
            await api.post(`/rooms/${roomId}/transcription/start`);

            // Get user's microphone with mono audio
            // Mobile-friendly constraints
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    // Mobile-specific constraints
                    sampleRate: { ideal: 16000 },
                    sampleSize: { ideal: 16 },
                }
            });
            audioStreamRef.current = stream;

            // Connect WebSocket (with auth token in the query string)
            const wsUrl = await getWsUrl();
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = async () => {
                console.log('Transcription WebSocket connected');
                setIsTranscribing(true);
                setIsConnecting(false);

                // Create AudioContext for processing
                // Handle Safari/iOS WebKit prefix
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                const audioContext = new AudioContextClass({
                    sampleRate: 16000 // Try to request 16kHz directly if possible
                });
                audioContextRef.current = audioContext;

                // Resume AudioContext (required for mobile browsers after user gesture)
                if (audioContext.state === 'suspended') {
                    await audioContext.resume();
                    console.log('AudioContext resumed (was suspended)');
                }

                // Get the actual sample rate (browser may not honor our request)
                const actualSampleRate = audioContext.sampleRate;
                console.log(`Audio context sample rate: ${actualSampleRate}`);

                // Create source node from microphone stream
                const source = audioContext.createMediaStreamSource(stream);
                sourceNodeRef.current = source;

                // Try AudioWorklet first (better for mobile), fallback to ScriptProcessor
                const workletSuccess = await setupAudioWorklet(audioContext, source, ws, actualSampleRate);
                if (!workletSuccess) {
                    setupScriptProcessor(audioContext, source, ws, actualSampleRate);
                }
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'transcript' && data.entries) {
                        setTranscript(prev => [...prev, ...data.entries]);
                    }
                } catch (e) {
                    console.error('Failed to parse transcript message:', e);
                }
            };

            ws.onerror = (event) => {
                console.error('WebSocket error:', event);
                setError('Connection error occurred');
                setIsConnecting(false);
            };

            ws.onclose = () => {
                console.log('Transcription WebSocket closed');
                setIsTranscribing(false);
                setIsConnecting(false);
            };

        } catch (err) {
            console.error('Failed to start transcription:', err);
            setError(err.message || 'Failed to start transcription');
            setIsConnecting(false);
        }
    }, [roomId, isTranscribing, isConnecting, getWsUrl, setupAudioWorklet, setupScriptProcessor]);

    // Stop transcription
    const stopTranscription = useCallback(async () => {
        // Stop AudioWorklet node
        if (workletNodeRef.current) {
            workletNodeRef.current.disconnect();
            workletNodeRef.current = null;
        }

        // Stop ScriptProcessor (fallback)
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }

        // Stop source node
        if (sourceNodeRef.current) {
            sourceNodeRef.current.disconnect();
            sourceNodeRef.current = null;
        }

        // Close audio context
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        // Stop audio stream
        if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
            audioStreamRef.current = null;
        }

        // Close WebSocket
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }

        // Call API to stop and save transcript
        try {
            await api.post(`/rooms/${roomId}/transcription/stop`);
        } catch (err) {
            console.error('Failed to stop transcription:', err);
        }

        setIsTranscribing(false);
    }, [roomId]);

    // Toggle transcription
    const toggleTranscription = useCallback(() => {
        if (isTranscribing) {
            stopTranscription();
        } else {
            startTranscription();
        }
    }, [isTranscribing, startTranscription, stopTranscription]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Cleanup resources when component unmounts
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            if (workletNodeRef.current) {
                workletNodeRef.current.disconnect();
                workletNodeRef.current = null;
            }
            if (processorRef.current) {
                processorRef.current.disconnect();
                processorRef.current = null;
            }
            if (sourceNodeRef.current) {
                sourceNodeRef.current.disconnect();
                sourceNodeRef.current = null;
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get transcript as text
    const getTranscriptText = useCallback(() => {
        return transcript.map(u => `${u.speaker}: ${u.text}`).join('\n');
    }, [transcript]);

    return {
        isTranscribing,
        isConnecting,
        transcript,
        error,
        startTranscription,
        stopTranscription,
        toggleTranscription,
        getTranscriptText,
        clearTranscript: () => setTranscript([]),
    };
}

export default useTranscription;
